import Image from 'next/image';
import React, { useMemo } from 'react';

import { FIXED_GLANCE_KEY } from '@common/constant/courseConstant';
import { useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { CourseFixedGlanceItem } from 'src/types/courseFixedGlanceItem';
import { LearningTypeId } from 'src/types/product';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { IconSize } from '@components/atoms/fontAwesomeIcon/constants';
import CourseAcademicFixedGlance from '@components/store/product/courseAcademicFixedGlance';
import CourseFixedGlance from '@components/store/product/courseFixedGlance';
import { convertDuration } from '@helpers/dateUtils';
import useCourseDetail from '@hooks/course/useCourseDetail';

const CourseFixedGlanceSection = () => {
  const { t } = useTranslation('store_course');

  const theme = useTheme();

  const {
    productLearningTypeId,
    hasCertificate,
    hasCertificateBundle,
    videoCount,
    countdownDay,
    totalVideoDuration,
    attachmentFileCount,
    quizCount,
    assignmentCount,
    examinationCount,
    articleCount,
    isCourseDetailLoading,
  } = useCourseDetail();

  const displayTotalVideoDuration = useMemo(() => {
    const hours = convertDuration(totalVideoDuration).toHour();
    const minutes = convertDuration(totalVideoDuration).toMinute();
    let value = '';
    if (hours > 0) {
      value = `${t('course_detail.course_description.course_fixed_glance.total_video.time.hour', { hours })} `;
    }
    if (minutes > 0) {
      value += t('course_detail.course_description.course_fixed_glance.total_video.time.minute', { minutes });
    }
    return value;
  }, [t, totalVideoDuration]);

  const isAcademic = productLearningTypeId === LearningTypeId.ACM;

  const iconSize = isAcademic ? IconSize.sm : IconSize.lg;

  const fixedGlances: CourseFixedGlanceItem[] = [];

  if (videoCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.VIDEO_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-circle-play" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.total_video', { total: videoCount }),
      subTitle: displayTotalVideoDuration,
    });
  }

  fixedGlances.push({
    id: FIXED_GLANCE_KEY.LIFE_TIME_LEARN,
    icon: !countdownDay ? (
      <FontAwesomeIcon icon="fa-light fa-infinity" color={theme.palette.primary.main} size={iconSize} />
    ) : (
      <Image src="/static/icons/glance-limit-time.svg" width="30px" height="35px" alt="limit-time" />
    ),

    title: !countdownDay
      ? t('course_detail.course_description.course_fixed_glance.no_expired_to_learn')
      : t('course_detail.course_description.course_fixed_glance.available_days_to_learn', { days: countdownDay }),
    subTitle: countdownDay
      ? t('course_detail.course_description.course_fixed_glance.available_days.subtitle')
      : undefined,
  });

  if (attachmentFileCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.ATTACHMENT_FILE_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-file-arrow-down" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.attachment_download'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_attachment_download', {
        total: attachmentFileCount,
      }),
    });
  }

  if (articleCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.ARTICLE_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-file-lines" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.article'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_article', { total: articleCount }),
    });
  }

  if (hasCertificateBundle) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.CERTIFICATE_BUNDLE,
      icon: <Image src="/static/icons/glance-certificate-bundle.svg" width="36px" height="36px" alt="certificate" />,
      title: t('course_detail.course_description.course_fixed_glance.has_learning_path'),
      subTitle: t('course_detail.course_description.course_fixed_glance.has_learning_path_subtitle'),
    });
  }

  if (hasCertificate || isAcademic) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.CERTIFICATE,
      icon: isAcademic ? (
        <Image src="/static/icons/glance-certificate.svg" width="15" height="17" alt="certificate" />
      ) : (
        <Image src="/static/icons/glance-certificate.svg" width="30px" height="35px" alt="certificate" />
      ),
      title: t('course_detail.course_description.course_fixed_glance.has_certificate'),
    });
  }

  if (quizCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.QUIZ_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-circle-question" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.quiz'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_quiz', { total: quizCount }),
    });
  }

  if (assignmentCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.ASSIGNMENT_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-pen" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.assignment'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_assignment', {
        total: assignmentCount,
      }),
    });
  }

  if (examinationCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.EXAMINATION_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-ballot-check" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.examination'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_examination', {
        total: examinationCount,
      }),
    });
  }

  if (isAcademic) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.MASTER_DEGREE,
      icon: <FontAwesomeIcon icon="fa-light fa-graduation-cap" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.masterDegree'),
    });
  }
  return (
    <>
      {isAcademic ? (
        <CourseAcademicFixedGlance isLoading={isCourseDetailLoading} glances={fixedGlances} />
      ) : (
        <CourseFixedGlance isLoading={isCourseDetailLoading} glances={fixedGlances} />
      )}
    </>
  );
};

export default CourseFixedGlanceSection;
